import { useEffect, useState } from 'react';
import axios from 'axios';
import { useHistory } from 'react-router-dom';
import Container from '../../components/Container';
import Header from '../../components/Header';
import Button from '../../components/Button';
import FlexContainer from '../../components/FlexContainer';
import businessDayString from '../../functions/businessDay';

interface TxRow {
    _id: string;
    date: string;
    guestID: string;
    fee: string;
    value: number;
    type?: string;
    paymentMethod?: string;
    quantity?: number;
    createdAt: string;
}

const dateInputToTxFormat = (iso: string) => {
    if (!iso) return '';
    const [y, m, d] = iso.split('-');
    return `${m}-${d}-${y}`;
};

const txFormatToDateInput = (s: string) => {
    if (!s) return '';
    const [m, d, y] = s.split('-');
    return `${y}-${m}-${d}`;
};

const SalesSummaryPage = () => {
    const history = useHistory();
    const [date, setDate] = useState<string>(businessDayString());
    const [allRows, setAllRows] = useState<TxRow[] | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        (async () => {
            setError(null);
            try {
                const { data } = await axios.get<TxRow[]>(
                    `/api/transactions?date=${date}`
                );
                setAllRows(data || []);
            } catch (e: any) {
                setError(e?.response?.data?.message || 'Failed to load.');
            }
        })();
    }, [date]);

    const rows = (allRows || []).filter((r) => r.guestID === 'SALE');

    let allAccessFees = 0;
    let limitedAccessFees = 0;
    (allRows || []).forEach((r) => {
        const v = Number(r.value) || 0;
        if (r.type === 'allAccess') allAccessFees += v;
        else if (r.type === 'limited' && r.guestID !== 'SALE')
            limitedAccessFees += v;
    });

    const totalsByItem: Record<string, { count: number; total: number }> = {};
    let grand = 0;
    let grandCount = 0;
    rows.forEach((r) => {
        const qty = Number(r.quantity) || 1;
        if (!totalsByItem[r.fee]) totalsByItem[r.fee] = { count: 0, total: 0 };
        totalsByItem[r.fee].count += qty;
        totalsByItem[r.fee].total += Number(r.value) || 0;
        grand += Number(r.value) || 0;
        grandCount += qty;
    });
    const itemRows = Object.keys(totalsByItem)
        .map((name) => ({ name, ...totalsByItem[name] }))
        .sort((a, b) => b.total - a.total);

    return (
        <Container containerName={'landing'}>
            <Header
                title={'Sales Summary'}
                subtitle={'End-of-shift cash-out'}
                bottomBorder={true}
                padding={true}
            />
            <FlexContainer>
                <span className="form-span">Date</span>
                <input
                    className="form-input"
                    type="date"
                    value={txFormatToDateInput(date)}
                    onChange={(e) => setDate(dateInputToTxFormat(e.target.value))}
                />

                <h3 className="transaction-container__text">
                    Date: <span className="red-span">{date}</span>
                </h3>

                {error && (
                    <h3 className="transaction-container__text">
                        <span className="red-span">{error}</span>
                    </h3>
                )}

                {allRows && (
                    <>
                        <h3 className="transaction-container__text">
                            <span className="red-span">Facility Use Fees Collected</span>
                        </h3>
                        <table className="transaction-container__form--table">
                            <thead>
                                <tr>
                                    <th>Type</th>
                                    <th>Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td className="transaction-container__form--table-stage">
                                        ALL ACCESS
                                    </td>
                                    <td className="transaction-container__form--table-value">
                                        ${allAccessFees.toFixed(2)}
                                    </td>
                                </tr>
                                <tr>
                                    <td className="transaction-container__form--table-stage">
                                        LIMITED ACCESS
                                    </td>
                                    <td className="transaction-container__form--table-value">
                                        ${limitedAccessFees.toFixed(2)}
                                    </td>
                                </tr>
                                <tr>
                                    <td className="transaction-container__form--table-stage">
                                        TOTAL FEES
                                    </td>
                                    <td className="transaction-container__form--table-value">
                                        $
                                        {(
                                            allAccessFees + limitedAccessFees
                                        ).toFixed(2)}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </>
                )}

                {rows && rows.length === 0 && (
                    <h3 className="transaction-container__text">
                        No kiosk sales yet.
                    </h3>
                )}

                {rows && rows.length > 0 && (
                    <>
                        <h3 className="transaction-container__text">
                            <span className="red-span">By Item</span>
                        </h3>
                        <table className="transaction-container__form--table">
                            <thead>
                                <tr>
                                    <th>Item</th>
                                    <th>Count</th>
                                    <th>Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {itemRows.map((i) => (
                                    <tr key={i.name}>
                                        <td className="transaction-container__form--table-stage">
                                            {i.name.toUpperCase()}
                                        </td>
                                        <td className="transaction-container__form--table-value">
                                            {i.count}
                                        </td>
                                        <td className="transaction-container__form--table-value">
                                            ${i.total.toFixed(2)}
                                        </td>
                                    </tr>
                                ))}
                                <tr>
                                    <td className="transaction-container__form--table-stage">
                                        TOTAL
                                    </td>
                                    <td className="transaction-container__form--table-value">
                                        {grandCount}
                                    </td>
                                    <td className="transaction-container__form--table-value">
                                        ${grand.toFixed(2)}
                                    </td>
                                </tr>
                            </tbody>
                        </table>

                        <h3 className="transaction-container__text">
                            <span className="red-span">All Sales</span>
                        </h3>
                        <table className="transaction-container__form--table">
                            <thead>
                                <tr>
                                    <th>Time</th>
                                    <th>Item</th>
                                    <th>Amount</th>
                                    <th>Payment</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r) => {
                                    const d = new Date(r.createdAt);
                                    let h = d.getHours();
                                    const m = d.getMinutes();
                                    const ampm = h >= 12 ? 'pm' : 'am';
                                    h = h % 12 || 12;
                                    const time = `${h}:${
                                        m < 10 ? '0' + m : m
                                    } ${ampm}`;
                                    return (
                                        <tr key={r._id}>
                                            <td className="transaction-container__form--table-time">
                                                {time}
                                            </td>
                                            <td className="transaction-container__form--table-stage">
                                                {r.fee.toUpperCase()}
                                                {r.quantity && r.quantity > 1
                                                    ? ` × ${r.quantity}`
                                                    : ''}
                                            </td>
                                            <td className="transaction-container__form--table-value">
                                                ${Number(r.value).toFixed(2)}
                                            </td>
                                            <td className="transaction-container__form--table-value">
                                                {(
                                                    r.paymentMethod || 'cash'
                                                ).toUpperCase()}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </>
                )}

                <Button
                    color={'red'}
                    margin={true}
                    onClick={() => history.push('/dashboard')}
                >
                    Back to Dashboard
                </Button>
            </FlexContainer>
        </Container>
    );
};

export default SalesSummaryPage;
